import { useCallback } from "react";
import { useParams } from "react-router-dom";
import { useGetKnowledgeDocuments } from "~/api/default/default";
import { useListProjects } from "~/api/folders/folders";
import { GetKnowledgeDocuments200ItemStatus } from "~/api/model";
import { useSupportiveMaterialsStore } from "~/features/cases/stores/useSupportiveMaterialsStore";

/**
 * The supportive-materials prompt is surfaced when the user reaches their
 * second non-demo case. By then they understand the product, so asking for
 * reference materials to make Sky AI output more bespoke lands naturally.
 *
 * It is intentionally skipped when the organization already has knowledge
 * documents (they've given us materials) or when the user has chosen to hide
 * it. Dismissal is stored on the device — no backend flag.
 */
const SECOND_CASE_THRESHOLD = 2;

export const useSupportiveMaterialsPrompt = () => {
  const { organizationId, caseId } = useParams<{
    organizationId: string;
    caseId: string;
  }>();
  const orgId = organizationId ?? "";

  const { data: casesData, isLoading: isCasesLoading } = useListProjects(
    orgId,
    { archived: false, limit: 50 },
    { query: { enabled: !!organizationId } }
  );

  const { data: knowledgeDocuments, isLoading: isKnowledgeLoading } =
    useGetKnowledgeDocuments(orgId, { query: { enabled: !!organizationId } });

  const hiddenOrganizationIds = useSupportiveMaterialsStore(
    (state) => state.hiddenOrganizationIds
  );
  const hide = useSupportiveMaterialsStore((state) => state.hide);

  const projects = casesData?.projects ?? [];
  const nonDemoCaseCount = projects.filter((c) => !c.isDemo).length;
  // Only count COMPLETED documents as usable materials. A row left in
  // UPLOADING/FAILED (e.g. an upload that errored before completing) is treated
  // as "still uploading" in Settings > Knowledge, so it shouldn't suppress the
  // prompt forever when the org has no usable materials yet.
  const hasKnowledgeDocuments = (knowledgeDocuments ?? []).some(
    (doc) => doc.status === GetKnowledgeDocuments200ItemStatus.COMPLETED
  );
  const isHidden = !!organizationId && hiddenOrganizationIds.includes(orgId);
  // Only surface the prompt on a real (non-demo) case page. Rendered on every
  // CasePage, so without this it could fire while the user is viewing a demo
  // case, spending the one-time prompt outside the intended second-case flow.
  const currentCase = projects.find((c) => c.id === caseId);
  const isViewingNonDemoCase = !!currentCase && !currentCase.isDemo;
  // Only prompt on a brand-new, empty case. If the case already has documents
  // the user is mid-work, so interrupting them with the upload modal is jarring
  // (and the materials ask belongs at the start of a case, not after).
  const isCurrentCaseEmpty = !!currentCase && currentCase.fileCount === 0;

  const shouldShow =
    !!organizationId &&
    !isCasesLoading &&
    !isKnowledgeLoading &&
    !isHidden &&
    !hasKnowledgeDocuments &&
    isViewingNonDemoCase &&
    isCurrentCaseEmpty &&
    nonDemoCaseCount === SECOND_CASE_THRESHOLD;

  const hidePermanently = useCallback(() => {
    if (organizationId) {
      hide(organizationId);
    }
  }, [hide, organizationId]);

  return { shouldShow, hidePermanently };
};
